import { QueryFactory, useDataBlockTypeByName } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { IconDropDownButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/IconDropDownButton';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { MenuDirection } from '@lmt/unicorn-shared/app/components/common/ui-kit/menu/MenuDirection';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import globalFormStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  caseInformationDataBlockTypeName,
  visitInformationDataBlockTypeName,
} from '@lmt/unicorn-shared/app/components/measurements/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import {
  CaseData,
  useCases,
} from '@lmt/unicorn-shared/app/helpers/case-queries';
import { useVisitOptions } from '@lmt/unicorn-shared/app/helpers/visit-queries';
import AddContentIcon from '@lmt/unicorn-shared/app/icons/add-content.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useEventCallback } from '@mui/material';
import React, { useMemo } from 'react';

import styles from './ContentButton.module.scss';

interface AddDocumentButtonProps {
  patientId: number;
  dataBlockId?: number;
  visitId?: number;
}

export const AddDocumentButton: React.FC<AddDocumentButtonProps> = (props) => {
  const forms = useGlobalForms();
  const tabs = useTabs();
  const i18next = useScopedTranslation('patients.documents');
  const match = pages.patientCases.useMatch();
  const modals = useModal();

  const caseInformationDataBlockType = useDataBlockTypeByName(
    caseInformationDataBlockTypeName,
  );
  const visitInformationDataBlockType = useDataBlockTypeByName(
    visitInformationDataBlockTypeName,
  );

  const { patientId, visitId, dataBlockId } = props;

  // if dataBlockId is provided, we can skip loading cases and visits as they won't be needed for the document creation flow
  const isEnabled = !props.dataBlockId;
  const casesQuery = useCases(patientId, isEnabled);
  const cases: CaseData[] = casesQuery?.data ?? [];
  const visits = useVisitOptions(patientId, isEnabled);

  const preselectedCaseId = useMemo(() => {
    if (!cases.length) {
      return null;
    }
    const now = Date.now();

    let closestPast: CaseData | null = null;
    let closestPastDiff = Infinity;

    let closestFuture: CaseData | null = null;
    let closestFutureDiff = Infinity;

    for (const c of cases) {
      const diff = new Date(c.startDate).getTime() - now;

      if (diff <= 0) {
        const absDiff = Math.abs(diff);
        if (absDiff < closestPastDiff) {
          closestPastDiff = absDiff;
          closestPast = c;
        }
      } else {
        if (diff < closestFutureDiff) {
          closestFutureDiff = diff;
          closestFuture = c;
        }
      }
    }

    return (closestPast ?? closestFuture)?.id ?? null;
  }, [cases]);

  const openDocumentCreation = useEventCallback(async () => {
    if (props.dataBlockId) {
      const caseId = match?.params?.caseId;
      const link = pages.createPatientDocument.link(
        {
          patientId: props.patientId,
        },
        { caseId, dataBlockId, visitId },
      );
      tabs.openTab(link);
      return;
    }

    const result = await selectCaseOrVisit('create');
    if (!result) {
      return null;
    }

    const link = pages.createPatientDocument.link(
      {
        patientId: props.patientId,
      },
      {
        caseId: result.caseId,
        visitId: result.visitId,
        dataBlockId: result.dataBlockId,
      },
    );
    tabs.openTab(link);
  });

  const openDocumentImport = useEventCallback(async () => {
    if (props.dataBlockId) {
      forms.importDocument(props.dataBlockId);
      return;
    }
    const result = await selectCaseOrVisit('import');
    if (!result) {
      return;
    }
    forms.importDocument(result.dataBlockId);
  });

  const selectCaseOrVisit = useEventCallback(
    async (operationType: 'create' | 'import') => {
      const result = await modals.showCustom<{
        caseId?: number | null;
        visitId?: number;
        errorText?: string;
      }>({
        title: i18next.t('create_document_modal.title'),
        defaultValue: {
          caseId: preselectedCaseId,
          visitId: undefined,
          errorText: '',
        },
        Component: (props) => {
          return (
            <div className={styles.content}>
              <span className={styles.text}>
                {operationType === 'import'
                  ? i18next.t('create_document_modal.import_text')
                  : i18next.t('create_document_modal.create_text')}
              </span>
              <Field
                title={i18next.t('create_document_modal.case_option')}
                className={globalFormStyles.field}
                required={true}
              >
                <DropDownInput
                  useIdFunctionAsValue={true}
                  value={props.value?.caseId}
                  options={cases}
                  idFunction={'id'}
                  getOptionLabel={'title'}
                  variant={'formInput'}
                  onValueChanged={(pCase) => {
                    props.setValue({
                      visitId: undefined,
                      caseId: pCase?.id,
                      errorText:
                        !!pCase?.id || !!props.value?.visitId
                          ? ''
                          : props.value?.errorText,
                    });
                  }}
                  errorText={props.value?.errorText}
                />
              </Field>
              <Field
                title={i18next.t('create_document_modal.visit_option')}
                className={globalFormStyles.field}
              >
                <DropDownInput
                  useIdFunctionAsValue={true}
                  value={props.value?.visitId}
                  options={visits.filter((x) =>
                    props.value?.caseId
                      ? x.caseId === props.value.caseId
                      : true,
                  )}
                  idFunction={'id'}
                  getOptionLabel={'title'}
                  variant={'formInput'}
                  onValueChanged={(visit) => {
                    props.setValue({
                      visitId: visit?.id,
                      caseId:
                        cases.find((x) => x.id === visit?.caseId)?.id ??
                        props.value?.caseId,
                      errorText:
                        !!visit?.id || !!props.value?.caseId
                          ? ''
                          : props.value?.errorText,
                    });
                  }}
                  errorText={props.value?.errorText}
                />
              </Field>
            </div>
          );
        },
        validate: (value) => {
          const isValid = !!value?.caseId || !!value?.visitId;
          if (value) {
            value.errorText = isValid
              ? ''
              : i18next.t('create_document_modal.required');
          }
          return isValid;
        },
        okButtonText:
          operationType === 'import'
            ? i18next.t('create_document_modal.import_button')
            : i18next.t('create_document_modal.create_button'),
      });
      if (!result?.caseId && !result?.visitId) {
        return null;
      }
      const dataBlocks = await QueryFactory.DataBlockClient.get(
        patientId,
        result?.caseId,
        result?.visitId,
      );
      // document should be attached to the information data block of a visit or case, so we need to find such data block for selected case/visit
      const informationDataBlock = dataBlocks?.data.find(
        (x) =>
          x.typeId === visitInformationDataBlockType?.id ||
          x.typeId === caseInformationDataBlockType?.id,
      );
      if (!informationDataBlock) {
        return null;
      }
      return {
        caseId: result.caseId,
        visitId: result.visitId,
        dataBlockId: informationDataBlock.id,
      };
    },
  );

  const addDocumentOptions = [
    {
      value: 'create',
      displayValue: i18next.t('add_button_create'),
      onClick: openDocumentCreation,
    },
    {
      value: 'import',
      displayValue: i18next.t('add_button_import'),
      onClick: openDocumentImport,
    },
  ];

  return (
    <IconDropDownButton
      color="blue"
      iconComponent={<AddContentIcon />}
      options={addDocumentOptions}
      optionListClassName={styles.optionListContainer}
      menuDirection={MenuDirection.bottomLeftEdge}
    />
  );
};
